/**
 * @fileoverview ต่ออายุ token อัตโนมัติขณะผู้ใช้ยังใช้งานอยู่
 * @module services/session
 *
 * token มีอายุ 7 วันนับจาก login และไม่เลื่อนตามการใช้งาน ผู้ใช้จึงถูกเตะออกทุกสัปดาห์
 * โมดูลนี้จะเรียก auth/refresh-token ล่วงหน้าเมื่ออายุเหลือน้อยกว่าเกณฑ์ ตราบใดที่ยังเปิดใช้งานอยู่
 * session จะไม่หมดอายุเอง ส่วน login ซ้อนจากที่อื่นยังเตะออกตามเดิม (backend เก็บ token 1 ค่าต่อ user)
 *
 * หลายแท็บใช้ localStorage ร่วมกัน จึงล็อกให้แท็บเดียว refresh ต่อรอบ และ backend มี grace window
 * ให้ token เดิมยังใช้ได้ชั่วครู่ กัน request ที่แท็บอื่นยิงค้างอยู่หลุด
 */

import { showAlert } from './api';
import { clearStorageKeepLang } from '../utils/backendMessage';

const TOKEN_KEY = 'cm-acc-tk';
const LOCK_KEY = 'cm-refresh-lock';
const LOCK_TTL_MS = 30 * 1000;
const CHECK_INTERVAL_MS = 60 * 1000;
/** เหลืออายุน้อยกว่านี้ให้ต่อ (token อายุ 7 วัน -> ผู้ใช้ที่เปิดใช้อย่างน้อยทุก 4 วันจะไม่หลุด) */
const REFRESH_BEFORE_EXPIRY_MS = 3 * 24 * 60 * 60 * 1000;

let inFlight = false;

const readToken = () => {
  try {
    const encoded = localStorage.getItem(TOKEN_KEY) || '';
    return encoded ? atob(encoded).replace('Bearer ', '') : '';
  } catch (e) {
    return '';
  }
};

const parseExp = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload?.exp ? payload.exp * 1000 : 0;
  } catch (e) {
    return 0;
  }
};

const acquireLock = () => {
  try {
    const now = Date.now();
    const existing = Number(localStorage.getItem(LOCK_KEY) || 0);
    if (existing && now - existing < LOCK_TTL_MS) return false;
    localStorage.setItem(LOCK_KEY, String(now));
    return true;
  } catch (e) {
    return true;
  }
};

const releaseLock = () => {
  try {
    localStorage.removeItem(LOCK_KEY);
  } catch (e) {}
};

/**
 * ตรวจอายุ token แล้วต่อให้ถ้าใกล้หมด
 * เงียบเมื่อ network/server มีปัญหา (รอบหน้าลองใหม่) แต่ถ้า backend ตอบว่า token ใช้ไม่ได้แล้ว
 * แปลว่าถูก login ซ้อน/ถูกปิดสิทธิ์ ให้ออกจากระบบเหมือน request ปกติ
 * @returns {Promise<boolean>} true เมื่อได้ token ใหม่
 */
export const refreshSessionIfNeeded = async () => {
  const token = readToken();
  if (!token || inFlight) return false;

  const exp = parseExp(token);
  if (!exp || exp <= Date.now() || exp - Date.now() > REFRESH_BEFORE_EXPIRY_MS) return false;

  if (!acquireLock()) return false;
  inFlight = true;

  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL}auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      cache: 'no-cache',
    });
    const result = await res.json().catch(() => null);

    if (result?.status === true && result.data?.token) {
      // อีกแท็บอาจ refresh ไปก่อนแล้ว: เขียนทับเฉพาะเมื่อยังเป็น token เดิมที่เราใช้ขอ
      if (readToken() === token) {
        localStorage.setItem(TOKEN_KEY, btoa(`Bearer ${result.data.token}`));
      }
      return true;
    }

    const message = result?.message || '';
    if (res.status === 401 && (message === 'Token not found' || message === 'Token has expired')) {
      clearStorageKeepLang();
      showAlert('/', 'warning', 'Warning', message);
    }
    return false;
  } catch (e) {
    return false;
  } finally {
    inFlight = false;
    releaseLock();
  }
};

/**
 * เริ่มตรวจอายุ token เป็นรอบ ๆ เรียกครั้งเดียวตอนแอปโหลด
 */
export const startSessionKeepAlive = () => {
  if (typeof window === 'undefined') return;
  refreshSessionIfNeeded();
  setInterval(refreshSessionIfNeeded, CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshSessionIfNeeded();
  });
};
